import type { BlockStyle, ResolvedStyle, Spacing } from "../types/style.js";
import type { DocumentStyles, TextStyle } from "../types/template.js";
import type { Theme } from "../types/theme.js";
import { resolveValue } from "./tokens.js";

export interface StyleCascadeInput {
  theme: Theme;
  styles: DocumentStyles;
  /** Document text style to inherit from. Defaults to "body". */
  textStyle?: string;
  /** Block definition defaults (lowest priority above document styles). */
  defaults?: BlockStyle;
  /** Instance overrides (highest priority). */
  overrides?: BlockStyle;
  /** When false, instance overrides are ignored (host permission). */
  allowOverrides?: boolean;
}

function spacing(base: Required<Spacing>, ...layers: Array<Spacing | undefined>): Required<Spacing> {
  const out = { ...base };
  for (const l of layers) {
    if (!l) continue;
    if (l.top !== undefined) out.top = l.top;
    if (l.right !== undefined) out.right = l.right;
    if (l.bottom !== undefined) out.bottom = l.bottom;
    if (l.left !== undefined) out.left = l.left;
  }
  return out;
}

/**
 * Resolves the style cascade for one block:
 *   theme defaults → document text style → block definition defaults → instance overrides.
 * Output contains only concrete values; renderers never see tokens.
 */
export function resolveStyle(input: StyleCascadeInput): ResolvedStyle {
  const { theme, styles } = input;
  const text: TextStyle = styles.text[input.textStyle ?? "body"] ?? styles.text.body ?? {};
  const d = input.defaults ?? {};
  const o = input.allowOverrides === false ? {} : input.overrides ?? {};

  const typo = theme.typography;
  const fontFamily = resolveValue(o.fontFamily ?? d.fontFamily ?? text.fontFamily, theme, typo.bodyFont);
  const fontSize = o.fontSize ?? d.fontSize ?? text.fontSize ?? typo.baseFontSize ?? 11;
  const fontWeight = o.fontWeight ?? d.fontWeight ?? text.fontWeight ?? typo.bodyWeight ?? 400;
  const lineHeight = o.lineHeight ?? d.lineHeight ?? text.lineHeight ?? typo.lineHeight ?? 1.5;

  const border = o.border || d.border
    ? {
        width: o.border?.width ?? d.border?.width ?? 1,
        style: o.border?.style ?? d.border?.style ?? "solid",
        color: resolveValue(o.border?.color ?? d.border?.color, theme, theme.colors.border),
        radius: o.border?.radius ?? d.border?.radius ?? 0,
        sides: o.border?.sides ?? d.border?.sides ?? ["top", "right", "bottom", "left"]
      }
    : undefined;

  const background = resolveOptional(o.background ?? d.background, theme);

  return {
    fontFamily,
    fontSize,
    fontWeight,
    fontStyle: o.fontStyle ?? d.fontStyle ?? text.fontStyle ?? "normal",
    lineHeight,
    letterSpacing: o.letterSpacing ?? d.letterSpacing ?? text.letterSpacing ?? 0,
    textTransform: o.textTransform ?? d.textTransform ?? text.textTransform ?? "none",
    textAlign: o.textAlign ?? d.textAlign ?? "left",
    verticalAlign: o.verticalAlign ?? d.verticalAlign ?? "top",
    color: resolveValue(o.color ?? d.color ?? text.color, theme, theme.colors.text),
    background,
    padding: spacing({ top: 0, right: 0, bottom: 0, left: 0 }, d.padding, o.padding),
    margin: spacing({ top: text.marginTop ?? 0, right: 0, bottom: text.marginBottom ?? 0, left: 0 }, d.margin, o.margin),
    border,
    width: o.width ?? d.width,
    height: o.height ?? d.height,
    align: o.align ?? d.align,
    opacity: o.opacity ?? d.opacity
  };
}

function resolveOptional(value: string | undefined, theme: Theme): string | undefined {
  if (!value || value === "transparent" || value === "none") return undefined;
  return resolveValue(value, theme, "");
}

/** Converts a resolved style to inline CSS declarations (used by every renderer). */
export function styleToCss(s: ResolvedStyle, opts: { text?: boolean; box?: boolean } = { text: true, box: true }): Record<string, string> {
  const css: Record<string, string> = {};
  if (opts.text !== false) {
    css["font-family"] = s.fontFamily;
    css["font-size"] = `${s.fontSize}px`;
    css["font-weight"] = String(s.fontWeight);
    css["font-style"] = s.fontStyle;
    css["line-height"] = String(s.lineHeight);
    if (s.letterSpacing) css["letter-spacing"] = `${s.letterSpacing}em`;
    if (s.textTransform !== "none") css["text-transform"] = s.textTransform;
    css["text-align"] = s.textAlign;
    css["color"] = s.color;
  }
  if (opts.box !== false) {
    if (s.background) css["background"] = s.background;
    const p = s.padding;
    if (p.top || p.right || p.bottom || p.left) css["padding"] = `${p.top}px ${p.right}px ${p.bottom}px ${p.left}px`;
    const m = s.margin;
    if (m.top || m.right || m.bottom || m.left) css["margin"] = `${m.top}px ${m.right}px ${m.bottom}px ${m.left}px`;
    if (s.border && s.border.width > 0 && s.border.style !== "none") {
      const decl = `${s.border.width}px ${s.border.style} ${s.border.color}`;
      if (s.border.sides.length === 4) css["border"] = decl;
      else for (const side of s.border.sides) css[`border-${side}`] = decl;
    }
    if (s.border?.radius) css["border-radius"] = `${s.border.radius}px`;
    if (s.width !== undefined) css["width"] = typeof s.width === "number" ? `${s.width}px` : s.width;
    if (s.height !== undefined) css["height"] = `${s.height}px`;
    if (s.opacity !== undefined && s.opacity < 1) css["opacity"] = String(s.opacity);
  }
  return css;
}

export function cssText(css: Record<string, string | number | undefined>): string {
  return Object.entries(css)
    .filter(([, v]) => v !== undefined && v !== "")
    .map(([k, v]) => `${k}:${v}`)
    .join(";");
}
